import { Component, inject, signal, WritableSignal } from '@angular/core';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  NavigationEnd,
} from '@angular/router';
import { AuthStore } from '../../../store/auth/auth.store';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IDRISORSECARD, IDRISORSESIDENAV } from '@core/utils/const';
import { AuthApiService } from '@core/services/auth.service';
import { MobileConfigService } from '@core/services/mobile/mobile-config.service';
import { TooltipModule } from 'primeng/tooltip';
import { tap, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';

@Component({
  selector: 'app-sidenav',
  imports: [
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    DrawerModule,
    TooltipModule,
    TranslatePipe,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
})
export class SidenavComponent {
  readonly authStore = inject(AuthStore);
  readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly mobileConfig = inject(MobileConfigService);

  public dashboard$: Subject<string> = new Subject();

  sideNavRoutesToShow: WritableSignal<
    {
      title: string;
      img: string;
      path: string;
      idCard?: string;
      idSidenav?: string;
      mobileOnly?: boolean;
    }[]
  > = signal([]);

  sideNavRoutes: {
    title: string;
    img: string;
    path: string;
    idCard?: string;
    idSidenav?: string;
    mobileOnly?: boolean;
  }[] = [
    {
      img: 'assets/icons/sidenav/Sidenav_home_v2.svg',
      path: '/home',
      title: 'sidenav.home',
    },
    {
      img: 'assets/icons/sidenav/Sidenav_dashboard_v2.svg',
      path: '/dashboard',
      title: 'sidenav.dashboard',
    },
    {
      img: 'building',
      path: '/distribuzione-anagrafica-soggetti',
      title: 'sidenav.registry_subject',
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.distribuzione_anagrafica_soggetti,
    },
    {
      img: 'users',
      path: '/clienti',
      title: 'sidenav.clients',
      idCard: IDRISORSECARD.clienti_ambiti_cantieri,
      idSidenav: IDRISORSESIDENAV.clienti,
    },
    {
      img: './assets/icons/sidenav/Sidenav_kit_v2.svg',
      path: '/kit',
      title: 'sidenav.kit',
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.kit,
    },
    {
      img: './assets/icons/sidenav/Sidenav_campagne_v2.svg',
      path: '/campagne-programmate',
      title: 'sidenav.scheduled_campaigns',
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.campagne,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_attività_v2.svg',
      path: '/attivita',
      title: 'sidenav.activity_management',
      idCard: IDRISORSECARD.distribuzione_censimento,
      idSidenav: IDRISORSESIDENAV.attivita,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_ambiti.svg',
      path: '/ambiti-tariffari',
      title: 'sidenav.tariff_scopes',
      idCard: IDRISORSECARD.clienti_ambiti_cantieri,
      idSidenav: IDRISORSESIDENAV.ambiti,
    },
    {
      img: 'building',
      path: '/anagrafica-soggetti',
      title: 'sidenav.registry_subject',
      idCard: IDRISORSECARD.soggetti_utenze_condomini,
      idSidenav: IDRISORSESIDENAV.anagrafica_soggetti,
    },
    {
      img: './assets/icons/sidenav/Sidenav_attrezzature_v2.svg',
      path: '/attrezzature',
      title: 'sidenav.attrezzature',
      idCard: IDRISORSECARD.attrezzature,
      idSidenav: IDRISORSESIDENAV.anagrafica,
    },
    {
      img: 'bolt',
      path: '/rfid-power',
      title: 'sidenav.rfid_power',
      mobileOnly: true,
    },
    {
      img: 'truck',
      path: '/flotta',
      title: 'sidenav.flotta',
      idCard: IDRISORSECARD.flotta,
      idSidenav: IDRISORSESIDENAV.flotta,
    },
    {
      img: 'building',
      path: '/dettaglio-conferimento-ccr',
      title: 'sidenav.transfer_details_ccr',
      idCard: IDRISORSECARD.ccr,
      idSidenav: IDRISORSESIDENAV.dettaglio_conferimenti_ccr,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_conferimenti_v2.svg',
      path: '/conferimenti',
      title: 'sidenav.transfer_details_ccr',
      idCard: IDRISORSECARD.ccr,
      idSidenav: IDRISORSESIDENAV.dettaglio_conferimenti,
    },
    {
      img: 'building',
      path: '/guida-raccolta',
      title: 'sidenav.collection_guide',
      idCard: IDRISORSECARD.sportello,
      idSidenav: IDRISORSESIDENAV.guida_raccolta,
    },
    {
      img: 'shopping-bag',
      path: '/lista-servizi',
      title: 'sidenav.service',
      idCard: IDRISORSECARD.servizi,
      idSidenav: IDRISORSESIDENAV.servizi_list,
    },
    {
      img: 'calendar-clock',
      path: '/servizi-giornalieri',
      title: 'sidenav.service',
      idCard: IDRISORSECARD.servizi,
      idSidenav: IDRISORSESIDENAV.servizi_list,
    },
    {
      img: 'building',
      path: '/calendario-raccolta',
      title: 'sidenav.harvest_calendar',
      idCard: IDRISORSECARD.sportello,
      idSidenav: IDRISORSESIDENAV.calendario_raccolta,
    },
    {
      img: './assets/icons/sidenav/Sidenav_ticket_v2.svg',
      path: '/gestione-richieste-crm',
      title: 'sidenav.harvest_calendar',
      idCard: IDRISORSECARD.crm,
      idSidenav: IDRISORSESIDENAV.gestione_ticket,
    },
    {
      img: 'bell',
      path: '/news-crm',
      title: 'sidenav.news',
      idCard: IDRISORSECARD.crm,
      idSidenav: IDRISORSESIDENAV.gestione_ticket,
    },
    {
      img: './assets/icons/sidenav/Sidenav_zone_v2.svg',
      path: '/tipologia-zona',
      title: 'sidenav.zonazione',
      idCard: IDRISORSECARD.zonazione,
      idSidenav: IDRISORSESIDENAV.tipologia_zona,
    },
    {
      img: 'assets/icons/sidenav/Sidenav_logout.svg',
      path: '',
      title: 'sidenav.sign_out',
    },
  ];

  readonly sessionIdRisorsa = sessionStorage.getItem('idRisorsa');

  /**
   * Filters routes based on platform (mobile vs web)
   * Hides mobile-only routes when on web platform
   * Shows mobile-only routes when on mobile platform
   */
  private filterRoutesByPlatform(
    routes: typeof this.sideNavRoutes,
  ): typeof this.sideNavRoutes {
    const isMobile = this.mobileConfig.isMobile();

    return routes.filter((route) => {
      if (route.mobileOnly) {
        return isMobile;
      }
      return true;
    });
  }

  // --- NUOVO METODO: Fonte di verità per la visibilità della Dashboard ---
  private shouldShowDashboard(): boolean {
    // 1. Recupera l'idCard salvato nel session storage
    const currentIdCard = sessionStorage.getItem('idRisorsa');

    // 2. Sulla rotta /home la dashboard non viene mai mostrata
    if (
      this.router.url.includes('/home') ||
      (this.router.url.includes('/guida-supporto') && !currentIdCard) ||
      (!environment.showDashboardFlotte && this.router.url.includes('/flotta'))
    ) {
      return false;
    }

    // 3. Nasconde la dashboard se ci troviamo nelle due sezioni specifiche
    if (
      currentIdCard === IDRISORSECARD.clienti_ambiti_cantieri ||
      currentIdCard === IDRISORSECARD.zonazione ||
      // mostrare la dashboard quando sarà pronta per i servizi
      currentIdCard === IDRISORSECARD.servizi
    ) {
      return false;
    }

    // In tutti gli altri casi, la dashboard deve essere visibile
    return true;
  }

  private updateSidenavRoutes(): void {
    const currentRoutes = [...this.sideNavRoutesToShow()];
    const dashboardIndex = currentRoutes.findIndex(
      (route) => route.path === '/dashboard',
    );

    // Verifichiamo la condizione tramite il nostro nuovo metodo
    const showDash = this.shouldShowDashboard();

    if (!showDash && dashboardIndex !== -1) {
      // Se NON deve essere mostrata ma è presente nella lista, la rimuove
      currentRoutes.splice(dashboardIndex, 1);
      this.sideNavRoutesToShow.set(currentRoutes);
    } else if (showDash && dashboardIndex === -1 && currentRoutes.length > 0) {
      // Se DEVE essere mostrata e non c'è, la inserisce subito dopo la "Home" (indice 1)
      currentRoutes.splice(1, 0, {
        img: 'assets/icons/sidenav/Sidenav_dashboard_v2.svg',
        path: '/dashboard',
        title: 'sidenav.dashboard',
      });
      this.sideNavRoutesToShow.set(currentRoutes);
    }
  }

  constructor() {
    this._setIdCantiereCliente();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateSidenavRoutes();
      });

    this.authStore.idRisorsa$
      .pipe(
        tap(() => {
          const user = this.authStore.user();
          const isAdmin = AuthApiService.isAdmin(user);

          const hasRequiredData = isAdmin
            ? sessionStorage.getItem('idCliente')
            : sessionStorage.getItem('idRisorsa') &&
              sessionStorage.getItem('idCantiere') &&
              sessionStorage.getItem('idCliente');

          if (hasRequiredData) {
            const listIdRisorseSidenav = JSON.parse(
              sessionStorage.getItem('listIdRisorseSidenav') || '[]',
            );

            const permissionFilteredRoutes = this.sideNavRoutes.filter(
              (route) =>
                listIdRisorseSidenav.some(
                  (res: any) => res.idRisorsa === route.idSidenav,
                ),
            );
            const platformFilteredRoutes = this.filterRoutesByPlatform(
              permissionFilteredRoutes,
            );

            const fullRoutes = [
              {
                img: 'assets/icons/sidenav/Sidenav_home_v2.svg',
                path: '/home',
                title: 'sidenav.home',
              },
              ...platformFilteredRoutes,
              {
                img: 'assets/icons/sidenav/Sidenav_logout.svg',
                path: '',
                title: 'sidenav.sign_out',
              },
            ];

            // Inserisce la dashboard subito dopo Home se necessario
            if (this.shouldShowDashboard()) {
              fullRoutes.splice(1, 0, {
                img: 'assets/icons/sidenav/Sidenav_dashboard_v2.svg',
                path: '/dashboard',
                title: 'sidenav.dashboard',
              });
            }

            this.sideNavRoutesToShow.set(fullRoutes);
          } else {
            const user = this.authStore.user();
            const isAdmin = AuthApiService.isAdmin(user);

            if (isAdmin) {
              this.sideNavRoutesToShow.set([
                {
                  img: 'assets/icons/sidenav/Sidenav_home_v2.svg',
                  path: '/home',
                  title: 'sidenav.home',
                },
                {
                  img: 'assets/icons/sidenav/Sidenav_logout.svg',
                  path: '',
                  title: 'sidenav.sign_out',
                },
              ]);
            } else {
              this.sideNavRoutesToShow.set([
                {
                  img: 'assets/icons/sidenav/Sidenav_logout.svg',
                  path: '',
                  title: 'sidenav.sign_out',
                },
              ]);
            }
          }
        }),
      )
      .subscribe();
  }

  private _setIdCantiereCliente(): void {
    const user = this.authStore.user();
    const isAdmin = AuthApiService.isAdmin(user);

    if (sessionStorage.getItem('idCliente')) {
      this.authStore.setSelectedIdCliente(sessionStorage.getItem('idCliente')!);

      if (sessionStorage.getItem('idCantiere')) {
        this.authStore.setSelectedIdCantiere(
          sessionStorage.getItem('idCantiere'),
        );
      } else if (!isAdmin) {
        this.router.navigate(['/home']);
        return;
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  shouldShowRoute(): boolean {
    const user = this.authStore.user();
    const isAdmin = AuthApiService.isAdmin(user);

    return isAdmin
      ? !!this.authStore.idClienteSelected()
      : !!(
          this.authStore.idCantiereSelected() &&
          this.authStore.idClienteSelected()
        );
  }

  async logout() {
    await this.authStore.logout();
  }
}
